<script setup lang="ts">
// @ts-expect-error — layout composable is JS without type declarations
import { useLayout } from '@/layout/composables/layout'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useMenuStore, type MenuNode } from '@/stores/menu'
import { useLocale } from '@/composables/useLocale'
import { usePwaInstall } from '@/composables/usePwaInstall'
import ChangePasswordDialog from '@/components/ChangePasswordDialog.vue'

const router = useRouter()
const auth = useAuthStore()
const menuStore = useMenuStore()
const { t, te } = useI18n()

function menuLabel(node: MenuNode): string {
  const key = `menu.${node.id}`
  return te(key) ? t(key) : node.label
}
const { locale, setLocale, locales, labels } = useLocale()

// Cambio password (dialog spostato qui dall'area utente in alto, ora rimossa).
const showChangePassword = ref(false)

// Selettore lingua raggruppato (bandierina della lingua attiva, espandibile).
const langOpen = ref(false)
const FLAGS: Record<string, string> = { it: '🇮🇹', en: '🇬🇧', fr: '🇫🇷', de: '🇩🇪' }
function flag(l: string): string { return FLAGS[l] ?? '🌐' }

// Installa app (PWA)
const pwa = usePwaInstall()
const iosHintOpen = ref(false)
async function onInstallaApp() {
  if (pwa.canInstall.value) {
    await pwa.install()
  } else {
    iosHintOpen.value = !iosHintOpen.value
    await nextTick()
    const menu = document.querySelector<HTMLElement>('.menu-transition')
    if (menu && layoutState.menuProfileActive) menu.style.maxHeight = menu.scrollHeight + 'px'
  }
}

// Foto dell'operatore loggato (stesso endpoint di "In servizio oggi"); fallback a icona se assente.
const fotoError = ref(false)
const fotoUrl = computed(() =>
  auth.me?.userId && !fotoError.value ? `/api/v2/personale/${auth.me.userId}/foto` : null,
)

// Base del portale classico (stesso pattern di AppMenu). Le voci del Menu Utente sono link
// legacy: aprono la pagina Seam (home.seam?pagina=<xhtml>) nella stessa scheda.
const legacyBase = import.meta.env.VITE_LEGACY_BASE_URL ?? '/legacy'

/** URL del portale classico per una voce legacy del menu utente. */
function legacyHref(node: MenuNode): string {
  return `${legacyBase}/home.seam?pagina=${encodeURIComponent(node.url ?? '')}`
}
const { layoutState, layoutConfig, isHorizontal, isSlim, isProfileMenuActive, isDesktop } =
  useLayout()
const outsideClickListener = ref<((e: Event) => void) | null>(null)

const menuClass = computed(() =>
  (isHorizontal as { value: boolean }).value ? 'overlay' : null
)

function toggleMenu() {
  const menu = document.querySelector<HTMLElement>('.menu-transition')
  if (!menu) return

  if ((isProfileMenuActive as { value: boolean }).value) {
    menu.style.maxHeight = '0'
    menu.style.opacity = '0'
    if ((isHorizontal as { value: boolean }).value) {
      menu.style.transform = 'scaleY(0.8)'
    }
  } else {
    menu.style.maxHeight = menu.scrollHeight + 'px'
    menu.style.opacity = '1'
    if ((isHorizontal as { value: boolean }).value) {
      menu.style.transform = 'scaleY(1)'
      if (isDesktop()) {
        layoutState.activePath = null
        layoutState.menuHoverActive = false
      }
    }
  }

  layoutState.menuProfileActive = !layoutState.menuProfileActive
}

const iconClass = computed(() => {
  const profilePositionStart = layoutConfig.menuProfilePosition === 'start'
  return {
    'pi-angle-up':
      ((isProfileMenuActive as { value: boolean }).value &&
        (profilePositionStart || (isHorizontal as { value: boolean }).value)) ||
      (!(isProfileMenuActive as { value: boolean }).value &&
        !profilePositionStart &&
        !(isHorizontal as { value: boolean }).value),
    'pi-angle-down':
      (!(isProfileMenuActive as { value: boolean }).value && profilePositionStart) ||
      ((isProfileMenuActive as { value: boolean }).value && !profilePositionStart) ||
      (isHorizontal as { value: boolean }).value,
  }
})

function tooltipValue(tooltipText: string): string | null {
  return (isSlim as { value: boolean }).value ? tooltipText : null
}

function unbindOutsideClickListener() {
  if (outsideClickListener.value) {
    document.removeEventListener('click', outsideClickListener.value)
    outsideClickListener.value = null
  }
}

function bindOutsideClickListener() {
  if (!outsideClickListener.value) {
    outsideClickListener.value = (event: Event) => {
      if (isOutsideClicked(event)) {
        toggleMenu()
      }
    }
    document.addEventListener('click', outsideClickListener.value)
  }
}

function isOutsideClicked(event: Event): boolean {
  const sidebarEl = document.querySelector('.layout-menu-profile')
  const topbarButtonEl = document.querySelector('.layout-menu-profile-button')
  const target = event.target as Node
  return !(
    sidebarEl?.isSameNode(target) ||
    sidebarEl?.contains(target) ||
    topbarButtonEl?.isSameNode(target) ||
    topbarButtonEl?.contains(target)
  )
}

// isProfileMenuActive is a ComputedRef<boolean> from the JS composable
watch(
  () => (isProfileMenuActive as unknown as { value: boolean }).value,
  (newVal: boolean) => {
    if (layoutConfig.menuMode === 'horizontal') {
      if (newVal) {
        bindOutsideClickListener()
      } else {
        unbindOutsideClickListener()
      }
    }
  }
)

onBeforeUnmount(() => {
  unbindOutsideClickListener()
})

// Quando espando/chiudo la lingua, ricalcolo l'altezza del dropdown aperto così le opzioni
// non vengono tagliate dalla max-height fissata all'apertura.
watch(langOpen, async () => {
  await nextTick()
  const menu = document.querySelector<HTMLElement>('.menu-transition')
  if (menu && layoutState.menuProfileActive) {
    menu.style.maxHeight = menu.scrollHeight + 'px'
  }
})

async function handleLogout() {
  await auth.logout()
  router.push('/login')
}

/*
 * NIENTE scorciatoia "diventa super admin".
 *
 * Ci abbiamo provato (badge a scudo accanto al passpartout, commit d0ddb6d2) ed era un errore:
 * `POST /auth/login-as` RIFIUTA di impersonare un account super-admin —
 * AuthService.loginAs, guardia "A-3": «Non è consentito impersonare un super-admin tramite
 * passpartout». È una protezione voluta contro l'escalation di privilegi: chi ha il passpartout
 * (tipicamente l'assistenza) non deve poter emettere un JWT con privilegi super-admin.
 * Per operare come super-admin serve autenticarsi con quelle credenziali, non passare dal
 * passpartout. Non rimuovere quella guardia per far funzionare un pulsante di comodità.
 */

// Click sul badge passpartout. Due esiti diversi, e il badge lo dice prima di agire:
//  • sessione nata da un «entra come…» → si TORNA all'operatore che ha impersonato, con la
//    chiave ancora attiva (prima si restava nell'utente impersonato, solo senza il flag, e per
//    tornare sé stessi bisognava rifare il login);
//  • sessione nata dalla password master → si resta questo utente e si perde la chiave.
const exitingPasspartout = ref(false)
const tornaA = computed(() => auth.me?.impersonatoDa ?? null)

/** Torna all'operatore che ha impersonato (la via del ritorno). */
async function tornaAllOperatore() {
  if (exitingPasspartout.value || !tornaA.value) return
  const ok = window.confirm(
    t(
      'layout.profilo.confermaTorna',
      { utente: tornaA.value },
      "Tornare a {utente}? Lascerai l'utente che stai impersonando e riprenderai il tuo account, con la chiave ancora attiva.",
    ),
  )
  if (!ok) return
  await eseguiUscita(true)
}

/**
 * Toglie la chiave RESTANDO nell'utente corrente.
 *
 * <p>Serve anche mentre si impersona qualcuno: è il modo di vedere il portale esattamente come
 * lo vede lui, senza privilegi residui che potrebbero mostrare o permettere qualcosa che a lui
 * non compare. È il motivo per cui le due azioni restano separate invece di sostituirsi.
 */
async function disattivaPasspartout() {
  if (exitingPasspartout.value) return
  const ok = window.confirm(
    tornaA.value
      ? t('layout.profilo.confermaTogliChiave', "Togliere la chiave restando come questo utente? Vedrai il portale con i suoi soli permessi, e per tornare te stesso dovrai rifare l'accesso.")
      : t('layout.profilo.confermaEsciPasspartout', 'Uscire dalla modalità passpartout? Resterai connesso come lo stesso utente, ma senza i privilegi passpartout.'),
  )
  if (!ok) return
  await eseguiUscita(false)
}

async function eseguiUscita(torna: boolean) {
  exitingPasspartout.value = true
  try {
    await auth.exitPasspartout(torna)
    // ricarica la pagina corrente così i dati riflettono i nuovi privilegi
    router.go(0)
  } finally {
    exitingPasspartout.value = false
  }
}
</script>

<template>
  <div class="layout-menu-profile">
    <button
      v-tooltip="{ value: tooltipValue(t('layout.profilo.profilo', 'Profilo')) }"
      class="layout-menu-profile-button"
      @click="toggleMenu"
    >
      <span class="profile-avatar">
        <img
          v-if="fotoUrl"
          :src="fotoUrl"
          :alt="t('layout.profilo.fotoProfilo', 'Foto profilo')"
          @error="fotoError = true"
        >
        <i
          v-else
          class="pi pi-user"
        />
      </span>
      <span class="text-start">
        <strong>{{ auth.me?.nomeCompleto?.trim() || auth.me?.username || t('layout.profilo.utente', 'Utente') }}</strong>
        <small>
          {{ auth.me?.isSuperAdmin ? t('layout.profilo.superAdmin', 'Super Admin') : t('layout.profilo.operatore', 'Operatore') }}
          <!-- Due azioni distinte, entrambe necessarie: tornare sé stessi (frequente) e
               togliere la chiave restando in questo utente (per vedere il portale come lo vede
               lui). Prima la seconda era l'unica; sostituirla con la prima l'avrebbe fatta
               perdere. -->
          <span
            v-if="auth.me?.passpartout"
            class="passpartout-badge"
          >
            <span
              v-if="tornaA"
              v-tooltip="{ value: t('layout.profilo.tornaA', { utente: tornaA }, 'Torna a {utente}') }"
              class="passpartout-badge__azione"
              role="button"
              tabindex="0"
              @click.stop="tornaAllOperatore"
              @keydown.enter.stop="tornaAllOperatore"
            >🔑 {{ t('layout.profilo.tornaA', { utente: tornaA }, 'Torna a {utente}').toLowerCase() }}</span>
            <span
              v-else
              class="passpartout-badge__azione"
            >🔑 {{ t('layout.profilo.passpartout', 'passpartout') }}</span>
            <span
              v-tooltip="{ value: tornaA
                ? t('layout.profilo.togliChiave', 'Togli la chiave restando come questo utente')
                : t('layout.profilo.esciPasspartout', 'Esci dalla modalità passpartout') }"
              class="passpartout-badge__azione passpartout-badge__chiudi"
              role="button"
              tabindex="0"
              @click.stop="disattivaPasspartout"
              @keydown.enter.stop="disattivaPasspartout"
            >✕</span>
          </span>
        </small>
      </span>
      <i
        class="layout-menu-profile-toggler pi pi-fw"
        :class="iconClass"
      />
    </button>

    <ul
      :class="['menu-transition', menuClass]"
      style="overflow: hidden; max-height: 0; opacity: 0"
    >
      <!-- Menu Utente (figli funzioni_2 "Menu Utente"): buste paga, documenti, timbrature,
           ferie/permessi. Voci legacy → aprono il portale classico; eventuali voci Vue future
           (isLegacy=false) usano router-link. -->
      <li
        v-for="node in menuStore.userItems"
        :key="node.id"
        v-tooltip="{ value: tooltipValue(menuLabel(node)) }"
      >
        <router-link
          v-if="!node.isLegacy && node.springRoute"
          :to="node.springRoute"
        >
          <i :class="[node.icon || 'pi pi-circle', 'pi-fw']" />
          <span>{{ menuLabel(node) }}</span>
        </router-link>
        <a
          v-else
          :href="legacyHref(node)"
          target="_self"
        >
          <i :class="[node.icon || 'pi pi-circle', 'pi-fw']" />
          <span>{{ menuLabel(node) }}</span>
        </a>
      </li>

      <!-- In fondo, prima di uscire: Lingua → Cambia password → Esci -->
      <!-- Lingua interfaccia (raggruppata: mostra la bandierina attiva, espande la scelta) -->
      <li>
        <button
          type="button"
          @click.stop="langOpen = !langOpen"
        >
          <span class="lang-flag pi-fw">{{ flag(locale) }}</span>
          <span>{{ t('userMenu.language', 'Lingua') }}: {{ labels[locale] }}</span>
          <i
            class="pi pi-fw ml-auto"
            :class="langOpen ? 'pi-angle-up' : 'pi-angle-down'"
          />
        </button>
      </li>
      <li
        v-for="l in locales"
        v-show="langOpen"
        :key="l"
        class="lang-option"
      >
        <button
          type="button"
          @click.stop="setLocale(l); langOpen = false"
        >
          <span class="lang-flag pi-fw">{{ flag(l) }}</span>
          <span>{{ labels[l] }}</span>
          <i
            v-if="locale === l"
            class="pi pi-check pi-fw ml-auto"
          />
        </button>
      </li>

      <!-- La mia firma digitale (firma remota): solo per chi è abilitato -->
      <li v-if="auth.has('FIRMA_REMOTA_USE')">
        <router-link to="/firma-digitale/mia">
          <i class="pi pi-verified pi-fw" />
          <span>{{ t('userMenu.firmaDigitale', 'La mia firma digitale') }}</span>
        </router-link>
      </li>

      <!-- Installa app (PWA): solo se il browser lo offre e l'app non è già installata.
           Su iOS (niente prompt nativo) il clic mostra il suggerimento «Condividi → Aggiungi a Home». -->
      <li v-if="pwa.visible.value">
        <button
          type="button"
          data-testid="installa-app"
          @click.stop="onInstallaApp"
        >
          <i class="pi pi-download pi-fw" />
          <span>{{ t('userMenu.installaApp', 'Installa app') }}</span>
        </button>
      </li>
      <li
        v-if="pwa.visible.value && iosHintOpen"
        class="install-hint"
      >
        <small>{{ t('userMenu.installaAppIos', "Su iPhone/iPad: tocca Condividi, poi «Aggiungi a Home».") }}</small>
      </li>

      <!-- Cambia password -->
      <li>
        <button
          type="button"
          @click.stop="showChangePassword = true"
        >
          <i class="pi pi-lock pi-fw" />
          <span>{{ t('userMenu.changePassword', 'Cambia password') }}</span>
        </button>
      </li>

      <li v-tooltip="{ value: tooltipValue(t('layout.profilo.logout', 'Logout')) }">
        <button @click="handleLogout">
          <i class="pi pi-power-off pi-fw" />
          <span>{{ t('layout.profilo.esci', 'Esci') }}</span>
        </button>
      </li>
    </ul>

    <ChangePasswordDialog v-model:visible="showChangePassword" />
  </div>
</template>

<style scoped>
.menu-transition {
  transition:
    max-height 400ms cubic-bezier(0.86, 0, 0.07, 1),
    opacity 400ms cubic-bezier(0.86, 0, 0.07, 1);
}
.menu-transition.overlay {
  transition:
    opacity 100ms linear,
    transform 120ms cubic-bezier(0, 0, 0.2, 1);
}
.passpartout-badge__azione {
  cursor: pointer;
}
.passpartout-badge__azione:hover {
  text-decoration: underline;
}
/* La ✕ è staccata dal testo: sono due comandi diversi e non devono sembrare uno solo. */
.passpartout-badge__chiudi {
  margin-left: 0.35rem;
  padding-left: 0.35rem;
  border-left: 1px solid currentColor;
  opacity: 0.85;
}

.passpartout-badge {
  display: inline-block;
  margin-left: 0.3rem;
  padding: 0 0.35rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  background: #7c3aed;
  color: #fff;
  letter-spacing: 0.02em;
}
.passpartout-badge--clickable {
  cursor: pointer;
}
.passpartout-badge--clickable:hover {
  background: #6d28d9;
}

/* Foto profilo nel pulsante (fallback a icona) */
.profile-avatar {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  overflow: hidden;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--p-surface-200);
}
.profile-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.profile-avatar .pi {
  font-size: 1.25rem;
}

.install-hint {
  padding: 0 1rem 0.5rem 2.5rem;
  color: var(--p-text-muted-color);
}

/* Selettore lingua */
.lang-flag {
  font-size: 1.05rem;
  line-height: 1;
}
.lang-option > button {
  padding-left: 2.5rem !important;
}
.ml-auto {
  margin-left: auto;
}
</style>
